import React from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  makeStyles,
  createStyles,
  Theme,
  Typography,
  IconButton,
  Grid,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import { XCircle } from 'react-feather';
import { colors } from '../../../theme';

type Props = {
  /** Header to show */
  headerTitle: React.ReactNode;
  /** Card Title */
  title: React.ReactNode;
  /** Content to display */
  content: React.ReactNode;
  /** Use content full width */
  contentFullWidth?: boolean;
  /** Instructions  */
  instructions: React.ReactNode;
  /** Set state for modal open or close */
  open: boolean;
  /** Function used to set open state to false */
  onCloseRequested: () => void;
  /**
   * Only provide context section of the id, the rest will be concatenated internally
   * e.g.: "legalentity" gives:
   * "design-system-button-legalentityinstructionheader"
   * "design-system-button-legalentityinstructiontitle"
   */
  id: string;
  /** Value used in the id prop to automatitation test */
  project?: string;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appBar: {
      backgroundColor: colors.madison,
      [theme.breakpoints.down('sm')]: {
        backgroundColor: colors.white,
      },
    },
    dynamicTextColor: {
      color: colors.white,
      [theme.breakpoints.down('sm')]: {
        color: colors.madison,
      },
    },
    appBarOffset: theme.mixins.toolbar,
    closeIcon: {
      position: 'absolute',
      top: '50%',
      marginTop: -12,
      right: theme.spacing(2),
    },
    contentWrapping: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
    },
    contentWrappingWithoutPadding: {
      marginTop: theme.spacing(2),
    },
    paddedText: {
      display: 'inline-block',
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      fontSize: 16,
    },
    dialoguePaper: {
      background: '#f9f9f9',
      position: 'relative',
      borderRadius: 10,
      [theme.breakpoints.down('sm')]: {
        borderRadius: 0,
      },
    },
    dialogueTitle: {
      // same as icon's width + right position
      marginRight: 24 + theme.spacing(2),
      marginLeft: 24 + theme.spacing(2),
    },
  }),
);

const InstructionsLayout = ({
  headerTitle,
  title,
  content,
  contentFullWidth = false,
  instructions,
  open,
  id,
  project = 'sales-web-inspection',
  onCloseRequested,
}: Props): JSX.Element => {
  const classes = useStyles();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      fullScreen={isMobile}
      maxWidth="xs"
      PaperProps={{
        className: classes.dialoguePaper,
      }}
      onClose={onCloseRequested}
    >
      <AppBar position="absolute" className={classes.appBar} elevation={0}>
        <Toolbar>
          <Grid
            container
            justifyContent="center"
            className={classes.dialogueTitle}
          >
            <Typography
              id={`${project}-${id}instructionheader`}
              variant="body1"
              className={classes.dynamicTextColor}
              align="center"
            >
              {headerTitle}
            </Typography>
          </Grid>
        </Toolbar>
        <IconButton
          id={`${project}-${id}-close-button`}
          data-testid="close-button"
          className={classes.closeIcon}
          onClick={onCloseRequested}
          title="close-button"
        >
          <XCircle className={classes.dynamicTextColor} />
        </IconButton>
      </AppBar>

      <div className={classes.appBarOffset} />
      <div
        data-testid="content-wrapper"
        className={
          contentFullWidth
            ? classes.contentWrappingWithoutPadding
            : classes.contentWrapping
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} container justifyContent="center">
            <Typography
              id={`${project}-label-${id}instructiontitle`}
              variant="body1"
              align="center"
            >
              {title}
            </Typography>
          </Grid>
          <Grid item xs={12} container justifyContent="center">
            {content}
          </Grid>
          <Grid item xs={12} container justifyContent="center">
            <div className={classes.paddedText}>{instructions}</div>
          </Grid>
        </Grid>
      </div>
    </Dialog>
  );
};

export default InstructionsLayout;
